<script setup>
import { onMounted, onUnmounted, ref, watch, nextTick } from "vue";
import interactjs from "interactjs";
import debounce from "lodash.debounce";
import { getImageInfo, isBlobUrl } from "../utils/preload-image";

const emit = defineEmits(["resize", "clearArtworkSize", "initSize"]);
const props = defineProps({
  artworkSize: {
    type: Object,
    required: true
  },
  oneInch: {
    type: Number,
    required: true
  },
  surfaceLabel: {
    type: String,
    required: true
  },
  artworkUrl: {
    type: String,
    required: true
  },
  surface: {
    type: Object,
    required: true
  }
});

const style = ref(null);
const designReferenceHandler = ref(null);
const designReference = ref(null);
const interact = ref(null);

const updateSize = () => {
  const rect = interact.value.getRect(designReference.value);
  emit("resize", { width: rect.width, height: rect.height });
};

const initSize = () => {
  const rect = interact.value.getRect(designReference.value);
  emit("initSize", { width: rect.width, height: rect.height });
};

const initDesign = async () => {
  try {
    const { ratio } = await getImageInfo(props.artworkUrl);

    if (!ratio) {
      throw new Error("Failed to calculate image ratio");
    }

    const minimalResizeImage =
      ratio > 1
        ? { width: props.oneInch * ratio, height: props.oneInch }
        : { width: props.oneInch, height: props.oneInch / ratio };

    interact.value = interactjs(designReferenceHandler.value).resizable({
      edges: { left: true, right: true, bottom: true, top: true },
      listeners: {
        move(event) {
          const { width, height } = event.rect;
          style.value = { width: `${width}px`, height: `${height}px` };
        },
        end: updateSize
      },
      restrictEdges: { outer: "parent", endOnly: true },
      modifiers: [
        interactjs.modifiers.aspectRatio({
          ratio,
          modifiers: [interactjs.modifiers.restrictSize({ max: "parent" })]
        }),
        interactjs.modifiers.restrictSize({ min: minimalResizeImage })
      ]
    });
    initSize();
  } catch (error) {
    emit("clearArtworkSize");
  }
};

let prevInnerWidth;
const resizeDesign = debounce(() => {
  const currentInnerWidth = window.innerWidth;
  if (currentInnerWidth === prevInnerWidth) return;
  emit("clearArtworkSize");
  nextTick(initDesign);
  prevInnerWidth = currentInnerWidth;
}, 1500);

watch(
  () => [props.artworkUrl, props.surfaceLabel],
  async ([newUrl], [oldUrl]) => {
    // Do not re-init design if we replace blob URL with replaced URL from the server
    if (!isBlobUrl(newUrl) && isBlobUrl(oldUrl)) return;
    emit("clearArtworkSize");
    await initDesign();
  }
);

watch(
  () => props.artworkSize,
  size => {
    if (size.width && size.height) {
      style.value = {
        width: `${size.width}px`,
        height: `${size.height}px`
      };
    } else {
      style.value = null;
    }
  }
);

onMounted(() => {
  initDesign();
  window.addEventListener("resize", resizeDesign);
});

onUnmounted(() => {
  if (interact.value) {
    interact.value.unset();
    interact.value = null;
  }
  window.removeEventListener("resize", resizeDesign);
});
</script>

<template>
  <img
    ref="designReference"
    data-testid="artwork-design-img"
    class="artwork-design-img"
    :src="props.artworkUrl"
    :style="style"
    :alt="`Artwork preview for ${props.surfaceLabel}`"
  />
  <div
    ref="designReferenceHandler"
    class="artwork-design-border"
    :style="style"
    role="application"
    aria-label="Resizable artwork border"
    tabindex="0"
  >
    <div
      class="artwork-design-border__handle artwork-design-border__handle--left"
      role="button"
      aria-label="Resize from left"
      tabindex="0"
    />
    <div
      class="artwork-design-border__handle artwork-design-border__handle--right"
      role="button"
      aria-label="Resize from right"
      tabindex="0"
    />
  </div>
</template>

<style scoped lang="scss">
@import "frontend/init/jiffy/mixin";
@import "frontend/init/pod/settings";

.artwork-design-img {
  display: block;
  user-select: none;
  margin: 0 auto;
  touch-action: none;
  max-width: 100%;
  max-height: 100%;
  pointer-events: none;
}

.artwork-design-border {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  outline: 2px solid $ultramarine-blue;
  touch-action: none;
  pointer-events: all;

  &::before {
    position: absolute;
    inset: 0;
    margin: -20px;
    content: "";

    @include with-desktop {
      display: none;
    }
  }

  &__handle {
    position: absolute;
    width: 20px;
    height: 20px;
    border: 2px solid $ultramarine-blue;
    background-color: $white;
    bottom: -11px;
    cursor: inherit;

    &--left {
      left: -11px;
    }

    &--right {
      right: -11px;
    }
  }
}
</style>
